import * as React from "react";

import type { InboxSummary } from "../../types/collaborateThread";
import { refetchCollaborateThreads } from "../../api";
import { useCollaborateStore } from "../../store/useCollaborateStore";
import { IconX } from "../icons";

/** Legacy key from the first implementation — clear so it cannot block new sessions. */
const LEGACY_DISMISS_SESSION_KEY = "cenna.collab.inboxWelcome.dismissed";

/**
 * In-memory only (clears on full refresh). Scoped to the current access token so
 * logout → login can offer the toast again.
 */
let offeredForToken: string | null = null;
let suppressedForToken: string | null = null;

/** Call on login/logout so the next auth session can show the welcome toast again. */
export function resetInboxWelcomeToast(): void {
  offeredForToken = null;
  suppressedForToken = null;
  try {
    sessionStorage.removeItem(LEGACY_DISMISS_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

export function formatInboxWelcomeMessage(summary: InboxSummary): string {
  const parts: string[] = [];
  if (summary.unreadMessages > 0) {
    parts.push(plural(summary.unreadMessages, "new message", "new messages"));
  }
  if (summary.mentions > 0) {
    parts.push(plural(summary.mentions, "mention", "mentions"));
  }
  if (summary.signoffs > 0) {
    parts.push(
      plural(summary.signoffs, "item awaiting your sign-off", "items awaiting your sign-off"),
    );
  }
  if (parts.length === 0) {
    return "You're all caught up.";
  }
  if (parts.length === 1) {
    return `You have ${parts[0]}.`;
  }
  if (parts.length === 2) {
    return `You have ${parts[0]} & ${parts[1]}.`;
  }
  return `You have ${parts[0]}, ${parts[1]} & ${parts[2]}.`;
}

function inboxHasItems(summary: InboxSummary | null | undefined): summary is InboxSummary {
  if (!summary) return false;
  return summary.unreadMessages > 0 || summary.mentions > 0 || summary.signoffs > 0;
}

export type InboxWelcomeToastProps = {
  summary: InboxSummary | null;
  active: boolean;
  /** Current auth access token — used to scope once-per-login offer/dismiss. */
  accessToken?: string | null;
  documentId?: string;
};

/**
 * Top toast when Collaborate loads with pending inbox items.
 *
 * When it shows:
 * - After login / cold start, once inbox-summary reports unread, mentions, or sign-offs
 * - Again after logout → login (auth session reset)
 * - Again after a full taskpane refresh (in-memory flags clear)
 *
 * When it stays hidden:
 * - User dismissed (×) or tapped View unread — until next login/refresh
 * - Inbox is empty
 * - Tab switches within the same session after it was already offered
 */
export const InboxWelcomeToast: React.FC<InboxWelcomeToastProps> = ({
  summary,
  active,
  accessToken = null,
  documentId,
}) => {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    // Drop leftover blocking key from the first implementation.
    try {
      sessionStorage.removeItem(LEGACY_DISMISS_SESSION_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  React.useEffect(() => {
    if (!active || !accessToken) {
      setVisible(false);
      return;
    }

    if (suppressedForToken === accessToken) {
      setVisible(false);
      return;
    }

    // Summary still loading — don't hide an already-visible toast on a brief null.
    if (summary == null) {
      return;
    }

    if (!inboxHasItems(summary)) {
      setVisible(false);
      return;
    }

    // Already offered once this auth session (e.g. user switched tabs) — don't nag.
    if (offeredForToken === accessToken) {
      return;
    }

    offeredForToken = accessToken;
    setVisible(true);
  }, [active, summary, accessToken]);

  const dismiss = React.useCallback(() => {
    if (accessToken) {
      suppressedForToken = accessToken;
      offeredForToken = accessToken;
    }
    setVisible(false);
  }, [accessToken]);

  const onViewUnread = React.useCallback(() => {
    const store = useCollaborateStore.getState();
    store.closeThread();
    store.setListView("unread");
    void refetchCollaborateThreads(documentId);
    dismiss();
  }, [dismiss, documentId]);

  if (!visible || !inboxHasItems(summary)) {
    return null;
  }

  return (
    <div className="inbox-welcome-toast" role="status" aria-live="polite">
      <p className="inbox-welcome-toast__message">{formatInboxWelcomeMessage(summary)}</p>
      <div className="inbox-welcome-toast__actions">
        <button type="button" className="inbox-welcome-toast__action" onClick={onViewUnread}>
          View unread
        </button>
        <button
          type="button"
          className="inbox-welcome-toast__dismiss"
          aria-label="Dismiss"
          onClick={dismiss}
        >
          <IconX size={14} />
        </button>
      </div>
    </div>
  );
};
